export function createAuthObserver(deps) {
  function maybeHandleLaunchRestNotification() {
    const launchRestPayload = deps.readLaunchRestNotificationPayload();
    if (launchRestPayload) {
      void deps.handleRestNotificationClickMessage(launchRestPayload);
    }
  }

  async function enterAuthenticatedApp(user, api, bootstrapData) {
    const resumeWorkoutId = String(bootstrapData?.activeSession?.workoutId || "").trim();

    if (resumeWorkoutId) {
      deps.setAuthSplashMessage("중단한 운동을 이어서 준비하고 있어요");
      await deps.refreshUserData({
        bootstrapData,
        preserveCurrentActiveSession: true,
      });
      await api.trackJourney("resume workout", async () => {
        await deps.openSession(resumeWorkoutId, {
          activeSession: bootstrapData.activeSession || null,
          previousByExercise: bootstrapData.previousByExercise || {},
          waitForPreviousByExercise: true,
          workout: bootstrapData.activeWorkout || null,
        });
      });
      deps.showAuthenticatedUI(user);
      deps.refreshAuthBadge();
      deps.hideAuthSplash({ message: "운동 화면을 준비했어요" });
      void deps.maybeAutoPrepareRestNotificationsOnLaunch();
      maybeHandleLaunchRestNotification();
      return;
    }

    deps.setAuthSplashMessage("홈 화면을 준비하고 있어요");
    await deps.refreshUserData({ bootstrapData });
    deps.showAuthenticatedUI(user);
    deps.refreshAuthBadge();
    deps.hideAuthSplash({ message: "홈 화면을 준비했어요" });
    void deps.maybeAutoPrepareRestNotificationsOnLaunch();
    maybeHandleLaunchRestNotification();
  }

  async function syncPendingSignupConsents(api, bootstrapData) {
    const signupConsents = deps.getPendingSignupConsents?.();
    if (!signupConsents || deps.hasRequiredLegalConsents(bootstrapData?.profile)) {
      return bootstrapData;
    }

    try {
      const profileResult = await api.updateProfile({ consents: signupConsents });
      deps.setPendingSignupConsents?.(null);
      if (profileResult?.profile) {
        return {
          ...bootstrapData,
          profile: profileResult.profile,
        };
      }
    } catch (_) {
      // Keep the consent gate available as a fallback if the silent sync fails.
    }

    return bootstrapData;
  }

  async function syncPendingSignupNickname(api) {
    const signupNickname = String(deps.getPendingSignupNickname() || "").trim();
    if (!signupNickname) {
      return;
    }

    try {
      const profileResult = await api.updateProfile({ nickname: signupNickname });
      deps.setPendingSignupNickname("");
      if (profileResult?.profile) {
        deps.setState({
          profile: profileResult.profile,
        });
      }
      deps.refreshAuthBadge();
    } catch (error) {
      deps.setPendingSignupNickname("");
      void deps.openAppAlertModal(
        error instanceof Error ? error.message : "회원 별명 저장에 실패했습니다",
        { title: "별명 저장 실패" },
      );
    }
  }

  return async function initAuthObserver() {
    const auth = deps.getAuth();
    if (!auth) {
      throw new Error("인증 클라이언트를 초기화하지 못했습니다");
    }

    auth.onAuthStateChanged(async (user) => {
      const api = deps.getApi();
      api?.clearCache?.();

      if (!user) {
        deps.resetObserverRuntimeState();
        deps.setAuthSplashMessage("로그인 화면을 준비하고 있어요");
        deps.setPendingSignupNickname("");
        deps.setPendingSignupConsents?.(null);
        const defaultLogsState = deps.createDefaultLogsState();
        await deps.unregisterRestPushSubscription({ silent: true }).catch(() => undefined);
        await deps.releaseScreenWakeLock();
        deps.setState({
          user: null,
          uid: "",
          token: "",
          clientPlatform: deps.resolveClientPlatform(),
          profile: null,
          settings: null,
          oneRM: null,
          estimatedOneRM: null,
          pushConfig: null,
          androidConfig: null,
          notificationCapabilities: null,
          pushPermission: deps.getNotificationPermissionState(),
          pushSubscriptionId: "",
          pushSubscriptionReady: false,
          androidBridgeReady: false,
          androidNotificationPermission: "default",
          androidExactAlarmAllowed: null,
          androidExactAlarmSupported: true,
          androidExactAlarmPermissionRequested: false,
          androidDeviceTokenReady: false,
          androidInstallationId: "",
          androidAppVersion: "",
          androidAppBuild: "",
          androidDeviceInfo: null,
          wakeLockActive: false,
          programs: [],
          activeProgram: null,
          workouts: [],
          activeSession: null,
          activeWorkout: null,
          sessionItemEditOrder: null,
          sessionItemReorderOrder: null,
          sessionPreviousItem: null,
          sessionPreviousByExercise: {},
          workoutDetailsById: {},
          homePreview: null,
          programBuilder: deps.createDefaultProgramBuilder(),
          programBuilderBaseline: null,
          programBuilderStage: "list",
          programBuilderDayIndex: 0,
          programTemplateQuery: "",
          exerciseNameMap: {},
          ...defaultLogsState,
          selectedTab: "home",
          sessionReturnTab: "home",
        });
        deps.showLoggedOutUI();
        deps.hideAuthSplash({ message: "로그인 화면을 준비하고 있어요" });
        return;
      }

      try {
        if (!api) {
          throw new Error("API 클라이언트를 초기화하지 못했습니다");
        }

        deps.setAuthSplashMessage("회원 정보를 불러오고 있어요");
        const token = await user.getIdToken();
        const defaultLogsState = deps.createDefaultLogsState();

        deps.setState({
          user,
          uid: user.uid,
          token,
          clientPlatform: deps.resolveClientPlatform(),
          pushPermission: deps.getNotificationPermissionState(),
          pushSubscriptionId: "",
          pushSubscriptionReady: false,
          androidExactAlarmPermissionRequested: false,
          analytics: {
            query: "",
            searchItems: [],
            selectedExerciseId: "",
            selectedExerciseName: "",
            volumeSeries: null,
          },
          homePreview: null,
          sessionPreviousItem: null,
          sessionPreviousByExercise: {},
          workoutDetailsById: deps.getState().workoutDetailsById || {},
          ...defaultLogsState,
          programBuilder: deps.getState().programBuilder || deps.createDefaultProgramBuilder(),
          programBuilderBaseline: deps.getState().programBuilderBaseline || null,
          programBuilderStage: deps.getState().programBuilderStage || "list",
          programBuilderDayIndex: deps.getState().programBuilderDayIndex || 0,
          programTemplateQuery: deps.getState().programTemplateQuery || "",
          exerciseNameMap: deps.getState().exerciseNameMap || {},
          selectedTab: "home",
          sessionReturnTab: "home",
        });

        let bootstrapData = await api.trackJourney("auth->home", async () => api.getBootstrap());
        bootstrapData = await syncPendingSignupConsents(api, bootstrapData);

        if (!deps.hasRequiredLegalConsents(bootstrapData?.profile)) {
          deps.setAuthSplashMessage("필수 동의 화면을 준비하고 있어요");
          deps.hideAuthSplash({ immediate: true, message: "필수 동의 화면을 준비했어요" });
          const consentResult = await deps.openConsentGate({
            prechecked: !!deps.getPendingSignupConsents?.(),
          });
          deps.setPendingSignupConsents?.(null);
          if (consentResult?.profile) {
            bootstrapData = {
              ...bootstrapData,
              profile: consentResult.profile,
            };
          }
        }

        await enterAuthenticatedApp(user, api, bootstrapData);
        deps.refreshAuthBadge();
        await syncPendingSignupNickname(api);
      } catch (error) {
        if (deps.isConsentGateCancelledError?.(error)) {
          return;
        }
        deps.showAuthenticatedUI(user);
        deps.refreshAuthBadge();
        deps.hideAuthSplash({ message: "홈 화면을 여는 중 문제가 있었어요" });
        void deps.openAppAlertModal(
          error instanceof Error ? error.message : "로그인 데이터 로딩에 실패했습니다",
          { title: "로그인 데이터 로딩 실패" },
        );
      }
    });
  };
}
